// /admin/ops — dashboard. Aggregate counters + per-task health table.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import { fetchDashboard, opsKeys } from "@/lib/ops-queries";

export const Route = createFileRoute("/_app/ops/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({ queryKey: opsKeys.dashboard, queryFn: fetchDashboard }),
  component: OpsDashboard,
});

function OpsDashboard() {
  const q = useQuery({
    queryKey: opsKeys.dashboard,
    queryFn: fetchDashboard,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Stat label="Open critical" value={q.data?.OpenCritical} tone="red" sub="any unresolved" />
        <Stat label="Open warning" value={q.data?.OpenWarning} tone="yellow" sub="any unresolved" />
        <Stat label="Open info" value={q.data?.OpenInfo} tone="gray" sub="any unresolved" />
        <Stat label="New critical (24h)" value={q.data?.NewLast24hCritical} tone="red" />
        <Stat label="New warning (24h)" value={q.data?.NewLast24hWarning} tone="yellow" />
        <Stat label="New info (24h)" value={q.data?.NewLast24hInfo} tone="gray" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Task health</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading && <Skeleton className="m-5 h-24" />}
          {q.data && (
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 text-left">Task</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Enabled</th>
                  <th className="px-4 py-2 text-left">Last run</th>
                  <th className="px-4 py-2 text-right">Last findings</th>
                  <th className="px-4 py-2 text-right">Open findings</th>
                </tr>
              </thead>
              <tbody>
                {q.data.Tasks.map((t) => (
                  <tr key={t.TaskDefID} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{t.Name}</td>
                    <td className="px-4 py-2">{t.TaskType}</td>
                    <td className="px-4 py-2">
                      <Badge tone={t.Enabled ? "success" : "muted"}>{t.Enabled ? "on" : "off"}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      {t.LastRunStatus ? (
                        <span>
                          <Badge tone={statusTone(t.LastRunStatus)}>{t.LastRunStatus}</Badge>{" "}
                          <span className="text-muted text-xs">{t.LastRunStartedAt ? fmtRelative(t.LastRunStartedAt) : "—"}</span>
                        </span>
                      ) : (
                        <span className="text-muted">never</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to="/ops/runs"
                        search={{ task_type: t.TaskType }}
                        className="text-fg hover:underline"
                      >
                        {t.LastRunFindingsNew}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to="/ops/findings"
                        search={{ task_type: t.TaskType, only_open: "true" }}
                        className="text-fg hover:underline"
                      >
                        {t.OpenFindings}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone, sub }: { label: string; value?: number; tone: "red" | "yellow" | "gray"; sub?: string }) {
  return (
    <Card>
      <CardBody>
        <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
        <div className={`mt-1 text-3xl font-display ${tone === "red" ? "text-red-500" : tone === "yellow" ? "text-yellow-500" : ""}`}>
          {value ?? "—"}
        </div>
        {sub && <div className="mt-1 text-xs text-muted">{sub}</div>}
      </CardBody>
    </Card>
  );
}

function statusTone(
  s: string,
): "success" | "danger" | "warning" | "muted" | "accent" {
  switch (s) {
    case "success":
      return "success";
    case "failed":
      return "danger";
    case "partial":
      return "warning";
    case "running":
      return "accent";
    case "cancelled":
      return "muted";
    default:
      return "muted";
  }
}
